/**
 * Sandbox-local helpers for CMS Scene publishing.
 *
 * Most scene-content helpers (download URLs, display names, the
 * anchor-width default, the load/attach orchestration) now live in
 * `trace-viewer/cms` so trace-sandbox, viewer-app, and TraceFrontend
 * can share them. This file keeps the publish-only bits that are
 * editor-specific (snapshotting + dirty detection).
 */

// ── Publish plan ────────────────────────────────────────────────────

/**
 * Snapshot of the scene's single publishable surface.
 *
 * `root` mirrors the document's export data — one root record and everything
 * below it (SCENE_ROOT_SPEC D8) — which is what the `RootContent` document
 * serializes. There used to be a second surface — an editor-managed
 * `"References"` node published as a `ReferenceContent` row — and it is gone:
 * the backend rejects that content type now (TraceBackend #915) and a library
 * reference is an ordinary node with an asset file URL for a `src`
 * (CONTENT_MODEL_SPEC §3.4), so it lives in the document like everything else.
 */
export interface PublishSnapshots {
    root: string | null;
}

export interface PublishPlan {
    rootDirty: boolean;
}

/**
 * Compute whether the scene needs publishing.
 *
 * The surface is dirty when its current snapshot differs from the
 * last-published snapshot. A null `last` snapshot is treated as dirty (no
 * baseline -> assume changed) only when a current snapshot exists;
 * `null === null` is clean.
 */
export function computePublishPlan(
    current: PublishSnapshots,
    last: PublishSnapshots,
): PublishPlan {
    return {
        rootDirty: current.root !== last.root,
    };
}
